import { useState, useEffect } from "react";

interface ContactProps {
  email: string;
}

const INBOUND_CHECKLIST = [
  { key: "00", text: "What you're building, in one paragraph." },
  { key: "01", text: "What's stuck or what needs to ship." },
  { key: "02", text: "Rough timeline & budget, if you have one." },
  { key: "03", text: "Anything else I should read first." },
];

export function Contact({ email }: ContactProps) {
  const [time, setTime] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const tick = () => {
      const formatted = new Date().toLocaleString("en-CA", {
        timeZone: "America/Vancouver",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
      setTime(`${formatted} PT`);
    };
    tick();
    const id = window.setInterval(tick, 30_000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 1600);
    return () => window.clearTimeout(id);
  }, [copied]);

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
    } catch {
      // Clipboard blocked (insecure context or denied permission) — the
      // address is visible and selectable regardless.
    }
  };

  return (
    <section id="contact" style={{ position: "relative", padding: "var(--section-y, 120px) 32px", overflow: "hidden" }}>
      {/* Oversized on purpose — clipped by the section. */}
      <div
        data-bleed
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          width: 900,
          height: 900,
          transform: "translate(-50%, -50%)",
          background: "radial-gradient(circle, color-mix(in oklab, var(--beam) 18%, transparent), transparent 60%)",
          pointerEvents: "none",
          filter: "blur(10px)",
        }}
      />

      <div className="container" style={{ position: "relative" }}>
        <div
          className="grid-2 contact-card"
          style={{
            background: "var(--surface)",
            border: "1px solid var(--hairline-strong)",
            borderRadius: "var(--r-2xl)",
            padding: 56,
            display: "grid",
            gridTemplateColumns: "minmax(0, 1.2fr) minmax(0, 1fr)",
            gap: 56,
            boxShadow: "var(--shadow-xl)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <svg
            viewBox="0 0 800 500"
            preserveAspectRatio="xMidYMid slice"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.06 }}
            aria-hidden
            data-bleed
          >
            <g stroke="var(--ink)" strokeWidth="1" fill="none">
              <rect x="100" y="180" width="600" height="140" rx="70" />
              <rect x="200" y="140" width="400" height="220" rx="110" />
              <rect x="280" y="100" width="240" height="300" rx="120" />
            </g>
          </svg>

          <div style={{ position: "relative", display: "flex", flexDirection: "column", gap: 24 }}>
            <span className="rule-eyebrow">Contact</span>
            <h2
              style={{
                margin: 0,
                fontFamily: "var(--font-display)",
                fontWeight: 300,
                fontSize: "clamp(36px, 5vw, 64px)",
                lineHeight: 1.0,
                letterSpacing: "-0.02em",
                color: "var(--ink)",
              }}
            >
              Send a short email.
              <br />
              <span style={{ color: "var(--ink-3)" }}>I'll write back.</span>
            </h2>
            <p style={{ margin: 0, maxWidth: 480, fontFamily: "var(--font-body)", fontSize: 16, lineHeight: 1.6, color: "var(--ink-2)" }}>
              Plain words, no forms. Tell me what you're building, what's stuck, and the rough timeline. A few sentences is plenty — I read every one.
            </p>
            <p style={{ margin: 0, maxWidth: 480, fontFamily: "var(--font-body)", fontSize: 16, lineHeight: 1.6, color: "var(--ink-2)" }}>
              And yes — I'm a real human. I build with AI all day, but your email lands with a person, and you'll get a human reply. Always.
            </p>

            <a
              href={`mailto:${email}?subject=${encodeURIComponent("Inbound · Slimsoft")}`}
              className="email-block"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 16,
                marginTop: 8,
                padding: "20px 22px",
                background: "var(--surface-2)",
                border: "1px solid var(--hairline-strong)",
                borderRadius: "var(--r-lg)",
                textDecoration: "none",
                color: "var(--ink)",
                transition:
                  "background var(--dur-base) var(--ease-out), border-color var(--dur-base) var(--ease-out)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "var(--surface-raised)";
                e.currentTarget.style.borderColor = "var(--beam)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "var(--surface-2)";
                e.currentTarget.style.borderColor = "var(--hairline-strong)";
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
                <svg
                  width="22" height="22" viewBox="0 0 24 24" fill="none"
                  stroke="var(--beam)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
                  style={{ flexShrink: 0 }}
                >
                  <rect width="20" height="16" x="2" y="4" rx="2" />
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                </svg>
                <span
                  className="email-address"
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 18,
                    fontWeight: 500,
                    color: "var(--ink)",
                    letterSpacing: "-0.01em",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {email}
                </span>
              </div>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontFamily: "var(--font-display)",
                  fontWeight: 500,
                  fontSize: 13,
                  color: "var(--beam)",
                  letterSpacing: "0.02em",
                  flexShrink: 0,
                }}
              >
                Write
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" />
                  <path d="m12 5 7 7-7 7" />
                </svg>
              </span>
            </a>

            <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", marginTop: 4 }}>
              <button
                type="button"
                onClick={copyEmail}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 12px",
                  borderRadius: "var(--r-md)",
                  background: "transparent",
                  border: "1px solid var(--hairline)",
                  color: copied ? "var(--bio)" : "var(--ink-3)",
                  fontFamily: "var(--font-mono)",
                  fontSize: 12,
                  letterSpacing: "0.02em",
                  cursor: "pointer",
                  transition: "all var(--dur-base) var(--ease-out)",
                }}
              >
                {copied ? (
                  <>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                    Copied
                  </>
                ) : (
                  <>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect width="14" height="14" x="8" y="8" rx="2" />
                      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                    </svg>
                    Copy address
                  </>
                )}
              </button>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--ink-4)" }}>
                <span className="live-dot" />
                {time} · Vancouver
              </span>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--ink-4)" }}>
                Replies in 24 h, weekdays
              </span>
            </div>
          </div>

          <div
            style={{
              position: "relative",
              background: "var(--surface-2)",
              border: "1px solid var(--hairline)",
              borderRadius: "var(--r-lg)",
              overflow: "hidden",
              alignSelf: "stretch",
            }}
          >
            <div
              style={{
                padding: "12px 16px",
                borderBottom: "1px solid var(--hairline)",
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: "var(--surface-raised)",
              }}
            >
              <span style={{ width: 9, height: 9, borderRadius: 999, background: "var(--ink-6)" }} />
              <span style={{ width: 9, height: 9, borderRadius: 999, background: "var(--ink-6)" }} />
              <span style={{ width: 9, height: 9, borderRadius: 999, background: "var(--ink-6)" }} />
              <span style={{ marginLeft: 8, fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--ink-4)" }}>
                ~ slimsoft / inbound.md
              </span>
            </div>
            <div
              style={{
                padding: "22px 22px",
                fontFamily: "var(--font-mono)",
                fontSize: 12,
                lineHeight: 1.7,
                color: "var(--ink-2)",
                display: "flex",
                flexDirection: "column",
                gap: 14,
              }}
            >
              <div>
                <span style={{ color: "var(--beam)" }}>$</span>{" "}
                <span style={{ color: "var(--ink-4)" }}>cat what-to-send.md</span>
              </div>
              {INBOUND_CHECKLIST.map((row) => (
                <div key={row.key} style={{ display: "flex", gap: 14 }}>
                  <span style={{ color: "var(--beam)" }}>{row.key}</span>
                  <span>{row.text}</span>
                </div>
              ))}
              <div style={{ marginTop: 8, paddingTop: 14, borderTop: "1px solid var(--hairline)", color: "var(--ink-3)" }}>
                <span style={{ color: "var(--ink-4)" }}># </span>
                You'll get a plain reply, not a sales sequence.
              </div>
              <div style={{ marginTop: 2, color: "var(--ink-4)" }}>
                <span style={{ color: "var(--beam)" }}>$</span>{" "}
                <span className="live-dot" style={{ display: "inline-block", verticalAlign: "middle", marginLeft: 4 }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
